import {
  getRsTimeframe,
  normalizeRsTimeframeKey,
  orderRsTimeframeKeys,
  RS_MAX_MULTI_TIMEFRAMES,
} from './relativeStrengthTimeframes';

export const RELATIVE_STRENGTH_SETTINGS_KEY = 'relativeStrengthSettings';

export const DEFAULT_RELATIVE_STRENGTH_SETTINGS = {
  mode: 'multi', // 'single' | 'multi'
  defaultTimeframe: 'today',
  multiTimeframes: ['15m', '1h', 'today'],
  historyTimeframe: 'month',
};

export function normalizeRelativeStrengthSettings(raw) {
  const mode = raw?.mode === 'single' || raw?.mode === 'multi'
    ? raw.mode
    : DEFAULT_RELATIVE_STRENGTH_SETTINGS.mode;
  const defaultTimeframe = normalizeRsTimeframeKey(
    raw?.defaultTimeframe || DEFAULT_RELATIVE_STRENGTH_SETTINGS.defaultTimeframe,
  );
  let multiTimeframes = orderRsTimeframeKeys(
    Array.isArray(raw?.multiTimeframes)
      ? raw.multiTimeframes
      : DEFAULT_RELATIVE_STRENGTH_SETTINGS.multiTimeframes,
  );
  if (multiTimeframes.length === 0) {
    multiTimeframes = orderRsTimeframeKeys(DEFAULT_RELATIVE_STRENGTH_SETTINGS.multiTimeframes);
  }
  multiTimeframes = multiTimeframes.slice(0, RS_MAX_MULTI_TIMEFRAMES);
  const historyTimeframe = normalizeRsTimeframeKey(
    raw?.historyTimeframe || DEFAULT_RELATIVE_STRENGTH_SETTINGS.historyTimeframe,
  );
  return {
    mode,
    defaultTimeframe,
    multiTimeframes,
    historyTimeframe,
  };
}

/**
 * TF Align: all finite scores agree above or below the 50 midpoint.
 * Returns 'up' | 'down' | null.
 */
export function relativeStrengthTfAlign(scores) {
  const values = (scores || []).filter((v) => Number.isFinite(v));
  if (values.length < 2) return null;
  const allUp = values.every((v) => v >= 50);
  const allDown = values.every((v) => v <= 50);
  if (allUp && values.some((v) => v > 50)) return 'up';
  if (allDown && values.some((v) => v < 50)) return 'down';
  if (allUp && allDown) return null;
  return null;
}

/**
 * Mild per-step boost so longer windows count a bit more in Combined.
 * Display order is shortest → longest; weights still follow period length.
 */
export const RS_COMPOSITE_WEIGHT_STEP = 0.3;

function compositeWeightIndex(i, listLength, timeframeKeys) {
  if (Array.isArray(timeframeKeys) && timeframeKeys[i]) {
    const ranks = timeframeKeys
      .map((key) => getRsTimeframe(key).periodRank)
      .sort((a, b) => a - b);
    const idx = ranks.indexOf(getRsTimeframe(timeframeKeys[i]).periodRank);
    if (idx >= 0) return idx;
  }
  // Fallback: scores follow display order (shortest → longest).
  return i;
}

/**
 * Multi-view Combined score: weighted mean of finite per-TF 0–100 scores
 * (already dial-mapped). Longer timeframes weigh a little more.
 * Returns null when nothing finite is available.
 *
 * @param {Array<number|null|undefined>} scores - same order as timeframeKeys / multiKeys
 * @param {string[]} [timeframeKeys] - optional; same length/order as scores
 */
export function relativeStrengthCompositeScore(scores, timeframeKeys) {
  const list = scores || [];
  let weightSum = 0;
  let valueSum = 0;
  for (let i = 0; i < list.length; i += 1) {
    const v = list[i];
    if (!Number.isFinite(v)) continue;
    const w = 1 + compositeWeightIndex(i, list.length, timeframeKeys) * RS_COMPOSITE_WEIGHT_STEP;
    valueSum += v * w;
    weightSum += w;
  }
  if (weightSum === 0) return null;
  return Math.round(valueSum / weightSum);
}
